import { ComboStoreComponent, HistoricalComboAthlete } from './filters-store';
import { HttpClient, HttpParams } from '@angular/common/http';
import { of, combineLatest } from 'rxjs';
import { environment } from '@environments/environment';
import { filter, tap, mergeMap, map, delay } from 'rxjs/operators';

/**
 * Interface for World Records combo data
 *
 * @export
 * @interface IWorldRecordsCombo
 */
export interface IWorldRecordsCombo {
  RecordDiscipline: number;
  RecordLevel: number;
  Category: number;
  CatCode: string;
  CatName: string;
  IsTeam: number;
}

/**
 * Combo for world records categories
 *
 * @export
 * @class WorldRecordsComboStore
 * @extends {WorldRecordsCategoriesComboStore<IWorldRecordsCombo>}
 */
export class WorldRecordsCategoriesComboStore extends ComboStoreComponent<IWorldRecordsCombo> {
  levelComboRef: WorldRecordsLevelComboStore;
  competitionDisciplinesRef: CompetitionDisciplinesComboStore;
  historicalComboRef: HistoricalComboAthlete;
  individualTeamComboRef: ComboStoreComponent<{ Name: string }>
  constructor(name_filter: string, id: string, http: HttpClient, extraOptions?: [], dependencies = [
    {
      entity: new WorldRecordsLevelComboStore('Level:', 'records-level-combo', http)
    },
    {
      entity: new CompetitionDisciplinesComboStore('Discipline:', 'records-discipline-combo', http, [])
    },
    {
      entity: new HistoricalComboAthlete('Time:', 'records-historic-combo', undefined, [
        {
          item: {
            Name: 'Current'
          },
          position: 0

        },
        {
          item: {
            Name: 'Historic'
          },
          position: 1

        },
      ])
    },
    {
      entity: new ComboStoreComponent('Event:', 'Name', 'records-event-combo', undefined, undefined, undefined, undefined, [
        {
          item: {
            Name: 'Individual'
          },
          position: 0

        },
        {
          item: {
            Name: 'Team'
          },
          position: 1

        },
      ], [], undefined, 'event')
    }
  ]) {
    super(name_filter, 'CatName', id, undefined, undefined, http, 'RECORDCATEGORIES', extraOptions, dependencies, true, 'category');
    this.filterInfo.options.initial_state = true;
    this.filterInfo.options.delay_time = 0;
    this.levelComboRef = dependencies[0].entity as any;
    this.competitionDisciplinesRef = dependencies[1].entity as any;
    this.historicalComboRef = dependencies[2].entity as any;
    this.individualTeamComboRef = dependencies[3].entity as any;
    this.defaultPipeForParams().pipe(
      mergeMap(a => super.getRequest(a, false))
    ).subscribe(next => {
      // Sort by name.
      this.data.sort((f, s) => {
        return f['CatName'].toLowerCase() > s['CatName'].toLowerCase() ? 1 : -1
      });
      this.dataStream.next(this.data);
    });
  }
  defaultPipeForParams() {
    return combineLatest(
      [this.levelComboRef.comboData.pipe(filter(a => a != undefined)),
      this.competitionDisciplinesRef.comboData.pipe(filter(a => a != undefined)),
      this.historicalComboRef.comboData,
      this.individualTeamComboRef.comboData]
    ).pipe(
      delay(100),
      map(([level, discipline, historical, individual]) => {
      return {
        RBP: 'ALL',
        DisciplineId: discipline.Id,
        RecordLevel: level.RecordLevelId,
        IndividualTeam: individual.Name === 'Individual' ? 1 : 2,
        Historical: historical.Name === 'Historic' ? 1 : 0
      }
    }))
  }
  getRequest(params?: any, request_more: boolean = false) {
    return this.defaultPipeForParams().pipe(
      mergeMap((paramsCustom) => {
        return super.getRequest(paramsCustom, false);
      })
    );
  }
}


/**
 * Interface for World Records areas combo data
 *
 * @export
 * @interface IWorldRecordsAreasCombo
 */
export interface IWorldRecordsAreasCombo {
  AreaId: string;
  Description: string;
  Level: string;
}

/**
 * Combo for world records areas categories
 *
 * @export
 * @class WorldRecordsComboStore
 * @extends {RecordsAreasCombo <IWorldRecordsAreasCombo>}
 */
export class RecordsAreasCombo extends ComboStoreComponent<IWorldRecordsAreasCombo> {
  constructor(name_filter: string, id: string, http: HttpClient, extraOptions?: []) {
    super(name_filter, 'Description', id, undefined, undefined, http, 'RECORDAREAS', extraOptions);
  }
  /**
 * A method for obtain combo data and set this propertly
 *
 * @param {*} params
 * @memberof ComboStoreComponent
 */
  protected getRequest(params: any, request_more: boolean = false) {
    if (this.http && this.endpoint_name) {
      return this.http.get<{ items: Array<IWorldRecordsAreasCombo> }>(environment.api_url, {
        params: new HttpParams({ fromObject: params })
          .append('content', this.endpoint_name)
          .append('v', '3')
      }).pipe(tap(next => {
        let data = [];
        if (this.extraOptions) {
          this.extraOptions.forEach(value => {
            next.items.splice(value.position, 0, value.item);
          });
        }
        data = next.items.filter(value => value.Level === '1' || value.Level === '2').reverse();
        if (this.comboInfo.options_subject) {

          this.comboInfo.options_subject.next(data);
          this.comboInfo.options_selected_subject.next(data[0]);

        } else {
          this.comboInfo.options_subject.next(data);
          this.comboInfo.options_selected_subject.next(data[0]);
        }
      })) as any;
    } else {
      this.comboInfo.options_subject.next(this.extraOptions);
      this.comboInfo.options_selected_subject.next(this.extraOptions[0]);
      return of({
        items: (this.extraOptions),
        pageInfo: { page: 1, resultsPerPage: this.extraOptions.length, totalResults: this.extraOptions.length }
      });
    }
  }
}


/**
 * Interface for World Records combo data
 *
 * @export
 * @interface IWorldHistoricRecordsCombo
 */
export interface IWorldHistoricRecordsCombo {
  name: string;
}

/**
 * Combo for world records categories
 *
 * @export
 * @class WorldRecordsComboStore
 * @extends {WorldRecordsCategoriesComboStore<IWorldHistoricRecordsCombo>}
 */
export class WorldRecordsHistoricComboStore extends ComboStoreComponent<IWorldHistoricRecordsCombo> {
  constructor(name_filter: string, id: string, extraOptions: [], dependencies?: []) {
    super(name_filter, 'name', id, undefined, undefined, undefined, undefined, extraOptions, dependencies);

  }
}

/**
 * Interface for World Records combo data
 *
 * @export
 * @interface IWorldEventRecordsCombo
 */
export interface IWorldEventRecordsCombo {
  name: string;
}

/**
 * Combo for world records categories
 *
 * @export
 * @class WorldRecordsComboStore
 * @extends {WorldRecordsCategoriesComboStore<IWorldEventRecordsCombo>}
 */
export class WorldRecordsEventHistoricComboStore extends ComboStoreComponent<IWorldEventRecordsCombo> {
  constructor(name_filter: string, id: string, http: HttpClient, extraOptions?: Array<any>) {
    super(name_filter, 'name', id, undefined, undefined, http, 'RECORDAREAS', extraOptions);

  }
}




/**
 * Interface for World Records combo data
 *
 * @export
 * @interface IWorldRecordsLevelCombo
 */
export interface IWorldRecordsLevelCombo {
  RecordLevelId: string;
  Description: string;
}

/**
 * Combo for world records categories
 *
 * @export
 * @class WorldRecordsComboStore
 * @extends {WorldRecordsCategoriesComboStore<IWorldRecordsLevelCombo>}
 */
export class WorldRecordsLevelComboStore extends ComboStoreComponent<IWorldRecordsLevelCombo> {
  constructor(name_filter: string = "Level", id: string = "record-level-combo", http: HttpClient) {
    super(name_filter, 'Description', id, undefined, undefined, http, 'RECORDLEVELS', [], [], undefined, 'level');

  }
  getRequest(params: any = { 'RBP': 'ALL' }) {
    return super.getRequest(params, false).pipe(
      tap(next => {
        const data = (next as any).items.filter(value => value.RecordLevelId === '1' || value.RecordLevelId === '2').reverse();
        if (this.comboInfo.options_subject) {
          this.comboInfo.options_subject.next(data);
          this.comboInfo.options_selected_subject.next(data[0]);

        } else {
          this.comboInfo.options_subject.next(data);
          this.comboInfo.options_selected_subject.next(data[0]);
        }
      })
    );
  }
}




/**
 * Interface for World Records combo data
 *
 * @export
 * @interface ICompetitionDisciplines
 */
export interface ICompetitionDisciplines {
  Id: string;
  Discipline: string;
  ViewOrder: string;
}

/**
 * Combo for world records categories
 *
 * @export
 * @class CompetitionDisciplinesComboStore
 * @extends {CompetitionDisciplines ComboStore<ICompetitionDisciplines>}
 */
export class CompetitionDisciplinesComboStore extends ComboStoreComponent<ICompetitionDisciplines> {
  constructor(name_filter: string, id: string, http: HttpClient, extraOptions: [], dependencies?: []) {
    super(name_filter, 'Discipline', id, undefined, undefined, http, 'COMPETITIONDISCIPLINES', extraOptions, dependencies, undefined, 'discipline');

  }
  /**
* A method for obtain combo data and set this propertly
*
* @param {*} params
* @memberof ComboStoreComponent
*/
  protected getRequest(params: any, request_more: boolean = false) {
    if (this.http && this.endpoint_name) {
      return this.http.get<{ items: Array<ICompetitionDisciplines> }>(environment.api_url, {
        params: new HttpParams({ fromObject: params })
          .append('content', this.endpoint_name)
          .append('v', '3')
      }).pipe(tap(next => {
        let data = [];
        if (this.extraOptions) {
          this.extraOptions.forEach(value => {
            next.items.splice(value.position, 0, value.item);
          });
        }
        data = next.items.filter(value => value.Id.toString() === '1' || value.Id.toString() === '2' || value.Id.toString() === '8');
        if (this.comboInfo.options_subject) {

          this.comboInfo.options_subject.next(data);
          this.comboInfo.options_selected_subject.next(data[0]);

        } else {
          this.comboInfo.options_subject.next(data);
          this.comboInfo.options_selected_subject.next(data[0]);
        }
      })) as any;
    } else {
      this.comboInfo.options_subject.next(this.extraOptions);
      this.comboInfo.options_selected_subject.next(this.extraOptions[0]);
      return of({
        items: (this.extraOptions),
        pageInfo: { page: 1, resultsPerPage: this.extraOptions.length, totalResults: this.extraOptions.length }
      });
    }
  }
}
